
import React, { lazy } from "react";
import { Routes, Route } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import SuperAdminRoute from "@/components/SuperAdminRoute";
import OwnerAdminRoute from "@/components/OwnerAdminRoute";
import { LearnProtectedRoute } from "@/components/learn/LearnProtectedRoute";
import { LearnAdminProtectedRoute } from "@/components/learn/LearnAdminProtectedRoute";
import { MASTER_ROUTES } from "./routes/masterRoutes";
import { siteauditRoutes } from "./routes/siteauditRoutes";
import { routes360 } from "./routes/360Routes";
import Index from "@/pages/Index";
import NotFound from "@/pages/NotFound";
import GoogleAuthCallbackPage from "@/pages/GoogleAuthCallbackPage";

// Auth Page
const AuthPage = lazy(() => import("@/pages/AuthPage"));

// Test Navigation Page
const TestNavigationPage = lazy(() => import("@/pages/TestNavigationPage"));

// Document to Page Builder (Experiment)
const DocumentToPageBuilderPage = lazy(() => import("@/pages/DocumentToPageBuilderPage"));

// Data Orchestrator (Experiment)
const DataOrchestratorPage = lazy(() => import("@/pages/experiments/DataOrchestratorPage"));

// OSDE Form (Experiment)
const OSDEPage = lazy(() => import("@/pages/experiments/OSDEPage"));
const OSDEDataReportPage = lazy(() => import("@/pages/experiments/OSDEDataReportPage"));

// Image Coordinates Experiment
const ImageCoordinatesExperimentPage = lazy(() => import("@/pages/ImageCoordinatesExperimentPage"));

// Collaborative Editor Experiment
const CollaborativeEditorExperiment = lazy(() => import("@/pages/experiments/CollaborativeEditorExperiment"));

// Experiments Index Page
const ExperimentsIndexPage = lazy(() => import("@/pages/experiments/ExperimentsIndexPage"));

// Dashboard Builder Experiment
const DashboardsExperimentPage = lazy(() => import("@/pages/experiments/DashboardsExperimentPage"));

// Incoming Transcripts Pages
const IncomingTranscriptsPage = lazy(() => import("@/pages/IncomingTranscriptsPage"));
const IncomingTranscriptsWebhookGuide = lazy(() => import("@/pages/IncomingTranscriptsWebhookGuide"));

// Personal Cards Pages
const PersonalCardsPage = lazy(() => import("@/pages/PersonalCardsPage"));
const PersonalCardPublicPage = lazy(() => import("@/pages/PersonalCards/PersonalCardPublicPage"));

// PDF Access & URL Shortener Pages
const PdfAccessSettings = lazy(() => import("@/pages/PdfAccessSettings"));
const PdfAccessStats = lazy(() => import("@/pages/PdfAccessStats"));
const ViewPrices = lazy(() => import("@/pages/ViewPrices"));
const UrlShortener = lazy(() => import("@/pages/UrlShortener"));
const UrlShortenerStats = lazy(() => import("@/pages/UrlShortenerStats"));
const ShortUrlRedirect = lazy(() => import("@/pages/ShortUrlRedirect"));

// Shared Annotation Report Page
const SharedAnnotationReportPage = lazy(() => import("@/pages/SharedAnnotationReportPage"));

// Shared Custom Report Page (public)
const SharedCustomReportPage = lazy(() => import("@/pages/SharedCustomReportPage"));

// Public Quiz Page
const PublicQuizPage = lazy(() => import("@/pages/PublicQuizPage"));

// Public Mini Game Page
const PublicMiniGamePage = lazy(() => import("@/pages/PublicMiniGamePage"));

// Public Poll Pages
const PollTakerPage = lazy(() => import("@/pages/PollTakerPage"));
const PollLeaderboardPublicPage = lazy(() => import("@/pages/PollLeaderboardPublicPage"));
const PollsPage = lazy(() => import("@/pages/PollsPage"));
const PollPreviewPage = lazy(() => import("@/pages/PollPreviewPage"));
const PollBuilderPage = lazy(() => import("@/pages/PollBuilderPage"));

// Meetings Pages
const MeetingsListPage = lazy(() => import("@/pages/meetings/MeetingsListPage"));
const CreateMeetingPage = lazy(() => import("@/pages/meetings/CreateMeetingPage"));
const MeetingDetailPage = lazy(() => import("@/pages/meetings/MeetingDetailPage"));
const MeetingTemplatesManager = lazy(() => import("@/pages/meetings/MeetingTemplatesManager"));
const MeetingTypesManager = lazy(() => import("@/pages/meetings/MeetingTypesManager"));
const MeetingSectionTypesManager = lazy(() => import("@/pages/meetings/MeetingSectionTypesManager"));
const MeetingSettingsPage = lazy(() => import("@/pages/meetings/MeetingSettingsPage"));
const VoteNowPage = lazy(() => import("@/pages/meetings/VoteNowPage"));

// Survey Pages
const SurveyTakerPage = lazy(() => import("@/pages/SurveyTakerPage"));
const SurveysPage = lazy(() => import("@/pages/SurveysPage"));
const SurveyBuilderPage = lazy(() => import("@/pages/SurveyBuilderPage"));
const SurveyDetailPage = lazy(() => import("@/pages/SurveyDetailPage"));
const SurveyEmailsPage = lazy(() => import("@/pages/SurveyEmailsPage"));
const SurveyResultsPage = lazy(() => import("@/pages/SurveyResultsPage"));
const SurveyRecipientsPage = lazy(() => import("@/pages/SurveyRecipientsPage"));
const SurveySubmissionsPage = lazy(() => import("@/pages/SurveySubmissionsPage"));
const SurveyResultsGlobalPage = lazy(() => import("@/pages/SurveyResultsGlobalPage"));
const SurveyEmailTrackingPage = lazy(() => import("@/pages/SurveyEmailTrackingPage"));
const SurveyProcessSubmissionsPage = lazy(() => import("@/pages/SurveyProcessSubmissionsPage"));
const SurveyEmailManagementPage = lazy(() => import("@/pages/SurveyEmailManagementPage"));
const SurveyPromptsPage = lazy(() => import("@/pages/SurveyPromptsPage"));

// Podcast Host Pages
const PodcastHostDashboard = lazy(() => import("@/pages/podcast-host/PodcastHostDashboard"));
const PodcastShowsPage = lazy(() => import("@/pages/podcast-host/PodcastShowsPage"));
const PodcastShowDetailPage = lazy(() => import("@/pages/podcast-host/PodcastShowDetailPage"));
const PodcastHostsPage = lazy(() => import("@/pages/podcast-host/PodcastHostsPage"));
const PodcastEpisodesPage = lazy(() => import("@/pages/podcast-host/PodcastEpisodesPage"));
const PodcastGuestsPage = lazy(() => import("@/pages/podcast-host/PodcastGuestsPage"));
const PodcastCampaignsPage = lazy(() => import("@/pages/podcast-host/PodcastCampaignsPage"));
const PodcastCampaignEmailsPage = lazy(() => import("@/pages/podcast-host/PodcastCampaignEmailsPage"));
const PodcastCampaignEmailSettingsPage = lazy(() => import("@/pages/podcast-host/PodcastCampaignEmailSettingsPage"));
const PodcastEmailAnalyticsPage = lazy(() => import("@/pages/podcast-host/PodcastEmailAnalyticsPage"));
const PodcastEmailSettingsPage = lazy(() => import("@/pages/podcast-host/PodcastEmailSettingsPage"));
const PodcastSettingsPage = lazy(() => import("@/pages/podcast-host/PodcastSettingsPage"));
const PodcastPromptsPage = lazy(() => import("@/pages/podcast-host/PodcastPromptsPage"));
const PodcastShowsEmbedGuide = lazy(() => import("@/pages/podcast-host/PodcastShowsEmbedGuide"));
const PodcastEpisodesEmbedGuide = lazy(() => import("@/pages/podcast-host/PodcastEpisodesEmbedGuide"));
const PodcastEpisodeEmbedGuide = lazy(() => import("@/pages/podcast-host/PodcastEpisodeEmbedGuide"));

// Events Module Pages
const EventsDashboard = lazy(() => import("@/pages/events/EventsDashboard"));
const EventsListPage = lazy(() => import("@/pages/events/EventsListPage"));
const EventDetailPage = lazy(() => import("@/pages/events/EventDetailPage"));
const EventSpeakersPage = lazy(() => import("@/pages/events/EventSpeakersPage"));
const EventLeadsPage = lazy(() => import("@/pages/events/EventLeadsPage"));
const EventCampaignsPage = lazy(() => import("@/pages/events/EventCampaignsPage"));
const EventCampaignEmailsPage = lazy(() => import("@/pages/events/EventCampaignEmailsPage"));
const EventCampaignEmailSettingsPage = lazy(() => import("@/pages/events/EventCampaignEmailSettingsPage"));
const EventPromptsPage = lazy(() => import("@/pages/events/EventPromptsPage"));
const EventAssociationsPage = lazy(() => import("@/pages/events/EventAssociationsPage"));
const EventAssociationTypesPage = lazy(() => import("@/pages/events/EventAssociationTypesPage"));
const EventCertificatesPage = lazy(() => import("@/pages/events/EventCertificatesPage"));
const EventsQuestionsPage = lazy(() => import("@/pages/events/EventsQuestionsPage"));
const EventCertificateTemplatesPage = lazy(() => import("@/pages/events/EventCertificateTemplatesPage"));
const EventEmailAnalyticsPage = lazy(() => import("@/pages/events/EventEmailAnalyticsPage"));
const EventsLeadsWebhookSettingsPage = lazy(() => import("@/pages/events/EventsLeadsWebhookSettingsPage"));
const EventsSettingsPage = lazy(() => import("@/pages/events/EventsSettingsPage"));
const EventsPublicPathPage = lazy(() => import("@/pages/events/EventsPublicPathPage"));
const EventsEmailSettingsPage = lazy(() => import("@/pages/events/EventsEmailSettingsPage"));
const EventsEmbedGuide = lazy(() => import("@/pages/events/EventsEmbedGuide"));
const EventEmbedGuide = lazy(() => import("@/pages/events/EventEmbedGuide"));

// Naming Pages
const NamingPage = lazy(() => import("@/pages/NamingPage"));
const NamingPromptsPage = lazy(() => import("@/pages/NamingPromptsPage"));

// Product Mapping Page
const ProductMappingPage = lazy(() => import("@/pages/ProductMappingPage"));

// Client Portal Pages
const ClientPortalAuthPage = lazy(() => import("@/pages/ClientPortalAuthPage"));
const ClientPortalDashboard = lazy(() => import("@/pages/ClientPortalDashboard"));  
const ClientPortalComments = lazy(() => import("@/pages/ClientPortalComments"));
const ClientPortalSaioDashboard = lazy(() => import("@/pages/ClientPortalSaioDashboard"));
const ClientPortalGoogleAdsPage = lazy(() => import("@/pages/ClientPortalGoogleAdsPage"));
const ClientPortalAnalyticsPage = lazy(() => import("@/pages/ClientPortalAnalyticsPage"));
const ClientPortalBusinessActivityPage = lazy(() => import("@/pages/ClientPortalBusinessActivityPage"));
const ClientPortalLeadsPage = lazy(() => import("@/pages/ClientPortalLeadsPage"));
const InstagramDataCollectorPage = lazy(() => import("@/pages/InstagramDataCollectorPage"));
import { LoadingFallback } from "@/components/ui/loading-fallback";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const GoogleAdsKeywordsPage = React.lazy(() => import("@/pages/GoogleAdsKeywordsPage"));
const GoogleAdsSearchTermsPage = React.lazy(() => import("@/pages/GoogleAdsSearchTermsPage"));

// SOP Task Assignment Pages
const SopAreaTaskAssignmentPage = React.lazy(() => import("@/pages/SopAreaTaskAssignmentPage"));
const SopTaskGroupTaskAssignmentPage = React.lazy(() => import("@/pages/SopTaskGroupTaskAssignmentPage"));

// OS Roadmap Page
const OSRoadmapPage = React.lazy(() => import("@/pages/OSRoadmapPage"));

// Release Notes Page
const ReleaseNotesPage = React.lazy(() => import("@/pages/ReleaseNotesPage"));

// Email Preview Page
const EmailPreviewPage = React.lazy(() => import("@/pages/EmailPreviewPage"));

// Email Designer Page
const EmailDesignerPage = React.lazy(() => import("@/pages/EmailDesignerPage"));

// Testimonials Pages
const TestimonialsPage = React.lazy(() => import("@/pages/TestimonialsPage"));
const TestimonialsEmbedGuide = React.lazy(() => import("@/pages/TestimonialsEmbedGuide"));
const TestimonialsWebhookGuide = React.lazy(() => import("@/pages/TestimonialsWebhookGuide"));
const SubmitTestimonialPage = React.lazy(() => import("@/pages/SubmitTestimonialPage"));
const PublicTestimonialsEmbed = React.lazy(() => import("@/pages/PublicTestimonialsEmbed"));

// Client Logos Pages
const ClientLogosPage = React.lazy(() => import("@/pages/ClientLogosPage"));
const ClientLogosEmbedGuide = React.lazy(() => import("@/pages/ClientLogosEmbedGuide"));
const PublicClientLogosEmbed = React.lazy(() => import("@/pages/PublicClientLogosEmbed"));

// Articles Pages
const ArticlesPage = React.lazy(() => import("@/pages/ArticlesPage"));
const ArticleEditorPage = React.lazy(() => import("@/pages/ArticleEditorPage"));
const ArticleCategoriesPage = React.lazy(() => import("@/pages/ArticleCategoriesPage"));
const ArticleTagsPage = React.lazy(() => import("@/pages/ArticleTagsPage"));
const ArticleAuthorsPage = React.lazy(() => import("@/pages/ArticleAuthorsPage"));
const ArticlesEmbedGuide = React.lazy(() => import("@/pages/ArticlesEmbedGuide"));
const ArticlesEmbedWidget = React.lazy(() => import("@/pages/ArticlesEmbedWidget"));
const MediaLibraryPage = React.lazy(() => import("@/pages/MediaLibraryPage"));
const ArticleTypographyPage = React.lazy(() => import("@/pages/ArticleTypographyPage"));
const AuthorsEmbedGuide = React.lazy(() => import("@/pages/AuthorsEmbedGuide"));
const TagsEmbedGuide = React.lazy(() => import("@/pages/TagsEmbedGuide"));
const CategoriesEmbedGuide = React.lazy(() => import("@/pages/CategoriesEmbedGuide"));

// Products Embed Guide
const ProductsEmbedGuide = React.lazy(() => import("@/pages/ProductsEmbedGuide"));

// CMS Plugins Pages
const CMSPluginsPage = React.lazy(() => import("@/pages/CMSPluginsPage"));

// LMS Prompts Page
const LmsPromptsPage = React.lazy(() => import("@/pages/LmsPromptsPage"));

// Internal Training Pages
const InternalTrainingPromptsPage = React.lazy(() => import("@/pages/InternalTrainingPromptsPage"));
const InternalTrainingDashboardPage = React.lazy(() => import("@/pages/InternalTrainingDashboardPage"));
const InternalTrainingProgramsPage = React.lazy(() => import("@/pages/InternalTrainingProgramsPage"));
const InternalTrainingProgramNewPage = React.lazy(() => import("@/pages/InternalTrainingProgramNewPage"));
const InternalTrainingProgramEditPage = React.lazy(() => import("@/pages/InternalTrainingProgramEditPage"));
const InternalTrainingAssignmentsPage = React.lazy(() => import("@/pages/InternalTrainingAssignmentsPage"));
const InternalTrainingAssignmentNewPage = React.lazy(() => import("@/pages/InternalTrainingAssignmentNewPage"));
const InternalTrainingProgressPage = React.lazy(() => import("@/pages/InternalTrainingProgressPage"));
const InternalTrainingSharesPage = React.lazy(() => import("@/pages/InternalTrainingSharesPage"));
const InternalTrainingShareGroupEditPage = React.lazy(() => import("@/pages/InternalTrainingShareGroupEditPage"));
const InternalTrainingShareSequencesPage = React.lazy(() => import("@/pages/InternalTrainingShareSequencesPage"));
const InternalTrainingSharesSettingsPage = React.lazy(() => import("@/pages/InternalTrainingSharesSettingsPage"));
const InternalTrainingProgramEmailsPage = React.lazy(() => import("@/pages/InternalTrainingProgramEmailsPage"));
const InternalTrainingNeedsPage = React.lazy(() => import("@/pages/InternalTrainingNeedsPage"));
const EvaluationCategoriesPage = React.lazy(() => import("@/pages/internal-training/EvaluationCategoriesPage"));
const EvaluationCriteriaPage = React.lazy(() => import("@/pages/internal-training/EvaluationCriteriaPage"));
const EmployeeEvaluationsPage = React.lazy(() => import("@/pages/internal-training/EmployeeEvaluationsPage"));

// SEO Revamb Pages
const SeoRevambPage = React.lazy(() => import("@/pages/SeoRevambPage"));
const SeoRevambAuditDetailPage = React.lazy(() => import("@/pages/SeoRevambAuditDetailPage"));

// HR Pages
const HREmployeesPage = React.lazy(() => import("@/pages/HREmployeesPage"));
const HREmployeeNewPage = React.lazy(() => import("@/pages/HREmployeeNewPage"));
const HREmployeeDetailPage = React.lazy(() => import("@/pages/HREmployeeDetailPage"));
const NoteworthyEventsPage = React.lazy(() => import("@/pages/human-resources/NoteworthyEventsPage"));
const NoteworthyEventTypesPage = React.lazy(() => import("@/pages/human-resources/NoteworthyEventTypesPage"));
const EmployeePositionsPage = React.lazy(() => import("@/pages/human-resources/EmployeePositionsPage"));
const OrgChartPage = React.lazy(() => import("@/pages/human-resources/OrgChartPage"));
const VacationManagementPage = React.lazy(() => import("@/pages/human-resources/VacationManagementPage"));
const OvertimeManagementPage = React.lazy(() => import("@/pages/human-resources/OvertimeManagementPage"));
const EmployeeWorkCalendarPage = React.lazy(() => import("@/pages/human-resources/EmployeeWorkCalendarPage"));

// Holdco Views Pages
const HoldcoViewsPage = React.lazy(() => import("@/pages/holdco-views/HoldcoViewsPage"));
const HoldcoAccessRequestsPage = React.lazy(() => import("@/pages/holdco-views/HoldcoAccessRequestsPage"));
const CreateHoldcoViewPage = React.lazy(() => import("@/pages/holdco-views/CreateHoldcoViewPage"));
const HoldcoViewDashboardPage = React.lazy(() => import("@/pages/holdco-views/HoldcoViewDashboardPage"));
const HoldcoDashboardComponentsPage = React.lazy(() => import("@/pages/holdco-views/HoldcoDashboardComponentsPage"));
const ValidateAccessRequestPage = React.lazy(() => import("@/pages/holdco-views/ValidateAccessRequestPage"));

// Custom Reports Pages
const CustomReportsPage = React.lazy(() => import("@/pages/custom-reports/CustomReportsPage"));
const CreateCustomReportPage = React.lazy(() => import("@/pages/custom-reports/CreateCustomReportPage"));
const CustomReportViewPage = React.lazy(() => import("@/pages/custom-reports/CustomReportViewPage"));

// Add Release Notes Admin Page
const AddReleaseNotesPage = React.lazy(() => import("@/pages/AddReleaseNotesPage"));

// Edit Release Notes Admin Page
const EditReleaseNotesPage = React.lazy(() => import("@/pages/EditReleaseNotesPage"));

// YouTube SEO Suggestions Results Page
const YouTubeSEOSuggestionsResultsPage = React.lazy(() => import("@/pages/YouTubeSEOSuggestionsResultsPage"));

// Metrics Aggregations Page
const MetricsAggregationsPage = React.lazy(() => import("@/pages/MetricsAggregationsPage"));
const MetricsConfigurationPage = React.lazy(() => import("@/pages/MetricsConfigurationPage"));
const MetricsDefinitionsPage = React.lazy(() => import("@/pages/admin/MetricsDefinitionsPage"));
const MetricsCockpitPage = React.lazy(() => import("@/pages/admin/MetricsCockpitPage"));

// Mobile Footer Config Page
const MobileFooterConfigPage = React.lazy(() => import("@/pages/MobileFooterConfigPage"));

// Business Activity Page
const BusinessActivityPage = React.lazy(() => import("@/pages/BusinessActivityPage"));

// SEO Test Results Page
const SeoTestResultsPage = React.lazy(() => import("@/pages/SeoTestResultsPage"));

// Trial Expired Page
const TrialExpiredPage = React.lazy(() => import("@/pages/TrialExpiredPage"));

// Home Page
const HomePage = React.lazy(() => import("@/pages/HomePage"));

// Consultant Application Page
const ConsultantApplicationPage = React.lazy(() => import("@/pages/ConsultantApplicationPage"));

// SEO Data Check Page
const SeoDataCheckPage = React.lazy(() => import("@/pages/SeoDataCheckPage"));

// SEO Internal Linking Analysis Pages
const InternalLinkingAnalysis = React.lazy(() => import("@/pages/seo/InternalLinkingAnalysis"));
const InternalLinkingCrawledPages = React.lazy(() => import("@/pages/seo/InternalLinkingCrawledPages"));
const InternalLinkingTokens = React.lazy(() => import("@/pages/seo/InternalLinkingTokens"));
const InternalLinkingEnrichedKeywords = React.lazy(() => import("@/pages/seo/InternalLinkingEnrichedKeywords"));
const InternalLinkingOpportunities = React.lazy(() => import("@/pages/seo/InternalLinkingOpportunities"));
const SeoVocabulary = React.lazy(() => import("@/pages/seo/SeoVocabulary"));
const SeoAiInsightsPage = React.lazy(() => import("@/pages/seo/SeoAiInsightsPage"));
const SeoPagesPage = React.lazy(() => import("@/pages/seo/SeoPagesPage"));
const SeoCannibalizationPage = React.lazy(() => import("@/pages/seo/SeoCannibalizationPage"));
const SeoKeywordClustersPage = React.lazy(() => import("@/pages/seo/SeoKeywordClustersPage"));
const SeoPageSpeedPage = React.lazy(() => import("@/pages/seo/SeoPageSpeedPage"));
const SeoContentBriefsPage = React.lazy(() => import("@/pages/seo/SeoContentBriefsPage"));
const SeoBacklinkIntelligencePage = React.lazy(() => import("@/pages/seo/SeoBacklinkIntelligencePage"));
const SeoContentIntelligencePage = React.lazy(() => import("@/pages/seo/SeoContentIntelligencePage"));
const SeoAnalyticsDashboardPage = React.lazy(() => import("@/pages/seo/SeoAnalyticsDashboardPage"));
const SeoKeywordsPage = React.lazy(() => import("@/pages/seo/SeoKeywordsPage"));

// Link Building Pages
const EmailTemplatesPage = React.lazy(() => import("@/pages/link-building/EmailTemplatesPage"));
const EmailSendsHistoryPage = React.lazy(() => import("@/pages/link-building/EmailSendsHistoryPage"));
const EmailAutomationSettingsPage = React.lazy(() => import("@/pages/link-building/EmailAutomationSettingsPage"));

// Plans and Billing Pages
const PlansAndBillingPage = React.lazy(() => import("@/pages/PlansAndBillingPage"));
const PlansAndBillingUsagePage = React.lazy(() => import("@/pages/PlansAndBillingUsagePage"));

// Badge Pages
const Badges = React.lazy(() => import("@/pages/Badges"));
const BadgeEditor = React.lazy(() => import("@/pages/BadgeEditor"));
const BadgeUsers = React.lazy(() => import("@/pages/BadgeUsers"));
const BadgeAnalytics = React.lazy(() => import("@/pages/BadgeAnalytics"));

// Fact Checker Pages
const FactCheckerDashboard = React.lazy(() => import("@/pages/FactCheckerDashboard"));
const FactCheckResults = React.lazy(() => import("@/pages/FactCheckResults"));
const MyFactChecks = React.lazy(() => import("@/pages/MyFactChecks"));
const FactCheckPromptsPage = React.lazy(() => import("@/pages/FactChecker/FactCheckPrompts"));
const BadgeClaimPage = React.lazy(() => import("@/pages/BadgeClaimPage"));

// Sellable Products Pages
const PaymentMethods = React.lazy(() => import("@/pages/PaymentMethods"));
const MyStore = React.lazy(() => import("@/pages/MyStore"));
const LMSDefaultPathsPage = React.lazy(() => import("@/pages/LMSDefaultPathsPage"));
const LMSPermalinksPage = React.lazy(() => import("@/pages/LMSPermalinksPage"));
const PublicPaymentApiGuide = React.lazy(() => import("@/pages/PublicPaymentApiGuide"));

// LMS Learn Portal Pages
const LearnAuthPage = React.lazy(() => import("@/pages/LearnAuthPage"));
const LearnLoginPage = React.lazy(() => import("@/pages/LearnLoginPage"));
const GRHotelsAuthPage = React.lazy(() => import("@/pages/GRHotelsAuthPage"));
const LearnVerifyPage = React.lazy(() => import("@/pages/LearnVerifyPage"));
const LearnDashboard = React.lazy(() => import("@/pages/LearnDashboard"));
const LearnCoursePage = React.lazy(() => import("@/pages/LearnCoursePage"));
const LearnLessonPage = React.lazy(() => import("@/pages/LearnLessonPage"));
const LearnAdminDashboard = React.lazy(() => import("@/pages/LearnAdminDashboard"));
const LearnAdminCourseList = React.lazy(() => import("@/pages/LearnAdminCourseList"));
const LearnAdminCourseEdit = React.lazy(() => import("@/pages/LearnAdminCourseEdit"));
const LearnAdminInstructors = React.lazy(() => import("@/pages/LearnAdminInstructors"));
const CourseTypesPage = React.lazy(() => import("@/pages/CourseTypesPage"));
const CloneGrowthOsCoursesPage = React.lazy(() => import("@/pages/CloneGrowthOsCoursesPage"));
const LearnAdminAnalytics = React.lazy(() => import("@/pages/LearnAdminAnalytics"));
const LearnAdminEmailAnalytics = React.lazy(() => import("@/pages/LearnAdminEmailAnalytics"));
const LearnAdminAssociations = React.lazy(() => import("@/pages/LearnAdminAssociations"));
const LearnAdminSettlements = React.lazy(() => import("@/pages/LearnAdminSettlements"));
const LearnAdminSettlementsNew = React.lazy(() => import("@/pages/LearnAdminSettlementsNew"));

// Public Course Marketplace Pages
const PublicCourseCatalog = React.lazy(() => import("@/pages/PublicCourseCatalog"));
const PublicCourseDetail = React.lazy(() => import("@/pages/PublicCourseDetail"));
const PublicCoursesEmbed = React.lazy(() => import("@/pages/PublicCoursesEmbed"));
const PublicEventsEmbed = React.lazy(() => import("@/pages/PublicEventsEmbed"));
const PublicEventEmbed = React.lazy(() => import("@/pages/PublicEventEmbed"));
const CoursesEmbedGuide = React.lazy(() => import("@/pages/CoursesEmbedGuide"));
const InstructorsEmbedGuide = React.lazy(() => import("@/pages/InstructorsEmbedGuide"));
const AcademiesEmbedGuide = React.lazy(() => import("@/pages/AcademiesEmbedGuide"));
const PromptsEmbedGuide = React.lazy(() => import("@/pages/PromptsEmbedGuide"));
const PublicAcademyCatalog = React.lazy(() => import("@/pages/PublicAcademyCatalog"));
const PublicAcademyDetail = React.lazy(() => import("@/pages/PublicAcademyDetail"));
const CourseCheckout = React.lazy(() => import("@/pages/CourseCheckout"));
const CoursePaymentSuccess = React.lazy(() => import("@/pages/CoursePaymentSuccess"));
const CoursePaymentCancel = React.lazy(() => import("@/pages/CoursePaymentCancel"));
const LearnAdminEnrollmentsPage = React.lazy(() => import("@/pages/LearnAdminEnrollmentsPage"));
const LearnAdminCompanyEnrollmentsPage = React.lazy(() => import("@/pages/LearnAdminCompanyEnrollmentsPage"));
const GRHotelsCheckout = React.lazy(() => import("@/pages/GRHotelsCheckout"));
const LearnAdminStripeConfig = React.lazy(() => import("@/pages/LearnAdminStripeConfig"));
const LearnAdminCertificateTemplates = React.lazy(() => import("@/pages/LearnAdminCertificateTemplates"));
const LearnAdminStudentEligibility = React.lazy(() => import("@/pages/LearnAdminStudentEligibility"));
const LearnAdminQuizManagement = React.lazy(() => import("@/pages/LearnAdminQuizManagement"));
const LearnAdminQuizSubmissions = React.lazy(() => import("@/pages/LearnAdminQuizSubmissions"));
const LearnAdminFeedback = React.lazy(() => import("@/pages/LearnAdminFeedback"));
const LearnAdminAskForMore = React.lazy(() => import("@/pages/LearnAdminAskForMore"));
const LearnAdminAcademies = React.lazy(() => import("@/pages/LearnAdminAcademies"));
const LearnAdminCourseTypes = React.lazy(() => import("@/pages/LearnAdminCourseTypes"));
const LMSWebhookSettings = React.lazy(() => import("@/pages/LMSWebhookSettings"));
const LMSNotificationSettings = React.lazy(() => import("@/pages/LMSNotificationSettings"));
const LmsDigitalCompetencesPage = React.lazy(() => import("@/pages/LmsDigitalCompetencesPage"));
const DigcompStatisticsPage = React.lazy(() => import("@/pages/digcomp/DigcompStatisticsPage"));
const DigcompOverviewPage = React.lazy(() => import("@/pages/digcomp/DigcompOverviewPage"));
const DigcompFrameworkPage = React.lazy(() => import("@/pages/digcomp/DigcompFrameworkPage"));
const DigcompStudentProgressPage = React.lazy(() => import("@/pages/digcomp/DigcompStudentProgressPage"));
const DigcompAssessmentsPage = React.lazy(() => import("@/pages/digcomp/DigcompAssessmentsPage"));
const DigcompBadgesCertificatesPage = React.lazy(() => import("@/pages/digcomp/DigcompBadgesCertificatesPage"));
const DigcompMappingOverviewPage = React.lazy(() => import("@/pages/digcomp/DigcompMappingOverviewPage"));
const LMSGeneralSettings = React.lazy(() => import("@/pages/LMSGeneralSettings"));
const LMSLeadsWebhookSettings = React.lazy(() => import("@/pages/LMSLeadsWebhookSettings"));
const LMSLeadsPage = React.lazy(() => import("@/pages/LMSLeadsPage"));
const LearnCertificatePage = React.lazy(() => import("@/pages/LearnCertificatePage"));
const LearnCertificateVerifyPage = React.lazy(() => import("@/pages/LearnCertificateVerifyPage"));
const LearningCenterSharesPage = React.lazy(() => import("@/pages/LearningCenterSharesPage"));
const LearningCenterSharesSettingsPage = React.lazy(() => import("@/pages/LearningCenterSharesSettingsPage"));
const PublicLmsProgressPage = React.lazy(() => import("@/pages/PublicLmsProgressPage"));
const PublicTrainingProgressPage = React.lazy(() => import("@/pages/PublicTrainingProgressPage"));

// Super Admin Pages
const SuperAdminDashboard = React.lazy(() => import("@/pages/super-admin/SuperAdminDashboard"));
const SuperAdminUsers = React.lazy(() => import("@/pages/super-admin/SuperAdminUsers"));
const SuperAdminBrands = React.lazy(() => import("@/pages/super-admin/SuperAdminBrands"));
const SuperAdminAnalytics = React.lazy(() => import("@/pages/super-admin/SuperAdminAnalytics"));
const SuperAdminSettings = React.lazy(() => import("@/pages/super-admin/SuperAdminSettings"));
const PinProtectionSettings = React.lazy(() => import("@/pages/super-admin/PinProtectionSettings"));

// Admin Support Dashboard
const SupportDashboard = React.lazy(() => import("@/pages/admin/SupportDashboard"));
const CannedResponsesPage = React.lazy(() => import("@/pages/admin/CannedResponsesPage"));
const SuperAdminActivity = React.lazy(() => import("@/pages/super-admin/SuperAdminActivity"));
const SuperAdminChatLogs = React.lazy(() => import("@/pages/super-admin/SuperAdminChatLogs"));
const GoogleAnnotationsPage = React.lazy(() => import("@/pages/super-admin/GoogleAnnotationsPage"));
const ProvisioningServices = React.lazy(() => import("@/pages/super-admin/ProvisioningServices"));
const ServicesProvisioned = React.lazy(() => import("@/pages/ServicesProvisioned"));
const ApifyUsagePage = React.lazy(() => import("@/pages/admin/ApifyUsagePage"));
const UserActivityTrackingPage = React.lazy(() => import("@/pages/admin/UserActivityTrackingPage"));
const UserTimeTrackingPage = React.lazy(() => import("@/pages/admin/UserTimeTrackingPage"));
const GrowthStandupAdminPage = React.lazy(() => import("@/pages/admin/GrowthStandupAdminPage"));
const ConsultantApplicationsPage = React.lazy(() => import("@/pages/admin/ConsultantApplicationsPage"));
const TeamCardsPage = React.lazy(() => import("@/pages/admin/TeamCardsPage"));
const VoiceAssistantToolsPage = React.lazy(() => import("@/pages/admin/VoiceAssistantToolsPage"));
const McpServerManagement = React.lazy(() => import("@/pages/admin/McpServerManagement"));
const McpServerDocs = React.lazy(() => import("@/pages/docs/McpServerDocs"));
const ReportingDashboardConfigPage = React.lazy(() => import("@/pages/ReportingDashboardConfigPage"));
const ThemeManagement = React.lazy(() => import("@/pages/super-admin/ThemeManagement"));
const QuotaManagementPage = React.lazy(() => import("@/pages/QuotaManagementPage"));

// Cold Outreach Pages
const ColdOutreachCampaignsPage = React.lazy(() => import("@/pages/ColdOutreachCampaignsPage"));
const ColdOutreachCampaignDetailPage = React.lazy(() => import("@/pages/ColdOutreachCampaignDetailPage"));
const ColdOutreachFlowViewPage = React.lazy(() => import("@/pages/ColdOutreachFlowViewPage"));
const ColdOutreachEmailSettingsPage = React.lazy(() => import("@/pages/ColdOutreachEmailSettingsPage"));
const ColdOutreachCentralEmailSettingsPage = React.lazy(() => import("@/pages/ColdOutreachCentralEmailSettingsPage"));
const ColdOutreachCreateCampaignPage = React.lazy(() => import("@/pages/ColdOutreachCreateCampaignPage"));
const ColdOutreachListsPage = React.lazy(() => import("@/pages/ColdOutreachListsPage"));
const ColdOutreachListDetailPage = React.lazy(() => import("@/pages/ColdOutreachListDetailPage"));
const ColdOutreachContactsPage = React.lazy(() => import("@/pages/ColdOutreachContactsPage"));
const ColdOutreachDispatchPage = React.lazy(() => import("@/pages/ColdOutreachDispatchPage"));
const ColdOutreachPromptsPage = React.lazy(() => import("@/pages/ColdOutreachPromptsPage"));
const ColdOutreachAnalyticsPage = React.lazy(() => import("@/pages/ColdOutreachAnalyticsPage"));
const ColdOutreachEmailAnalyticsPage = React.lazy(() => import("@/pages/ColdOutreachEmailAnalyticsPage"));
const ColdOutreachEmailProvidersPage = React.lazy(() => import("@/pages/ColdOutreachEmailProvidersPage"));

// Referral System Pages
const ReferralContactsPage = React.lazy(() => import("@/pages/referral/ReferralContactsPage"));
const ReferralProgramsPage = React.lazy(() => import("@/pages/referral/ReferralProgramsPage"));
const ReferralProgramDetailPage = React.lazy(() => import("@/pages/referral/ReferralProgramDetailPage"));
const ReferralCampaignsPage = React.lazy(() => import("@/pages/referral/ReferralCampaignsPage"));
const ReferralCreateCampaignPage = React.lazy(() => import("@/pages/referral/ReferralCreateCampaignPage"));
const ReferralCampaignDetailPage = React.lazy(() => import("@/pages/referral/ReferralCampaignDetailPage"));
const ReferralCampaignEmailsPage = React.lazy(() => import("@/pages/referral/ReferralCampaignEmailsPage"));
const ReferralDispatchPage = React.lazy(() => import("@/pages/referral/ReferralDispatchPage"));
const ReferralPromptsPage = React.lazy(() => import("@/pages/referral/ReferralPromptsPage"));
const ReferralAnalyticsPage = React.lazy(() => import("@/pages/referral/ReferralAnalyticsPage"));
const ReferralJoinPage = React.lazy(() => import("@/pages/referral/ReferralJoinPage"));
const ReferralListsPage = React.lazy(() => import("@/pages/referral/ReferralListsPage"));
const ReferralListDetailPage = React.lazy(() => import("@/pages/referral/ReferralListDetailPage"));
const ReferralParticipantsFeedPage = React.lazy(() => import("@/pages/referral/ReferralParticipantsFeedPage"));
const ReferralParticipantsEmbedPage = React.lazy(() => import("@/pages/referral/ReferralParticipantsEmbedPage"));
const ReferralLeaderboardEmbedPage = React.lazy(() => import("@/pages/referral/ReferralLeaderboardEmbedPage"));
const ReferralShareEmbedPage = React.lazy(() => import("@/pages/referral/ReferralShareEmbedPage"));
const ReferralJoinEmbedPage = React.lazy(() => import("@/pages/referral/ReferralJoinEmbedPage"));
const ReferralEmailSettingsPage = React.lazy(() => import("@/pages/referral/ReferralEmailSettingsPage"));
const ReferralEmailAnalyticsPage = React.lazy(() => import("@/pages/referral/ReferralEmailAnalyticsPage"));
const ReferralControlCenterPage = React.lazy(() => import("@/pages/public/ReferralControlCenterPage"));
const EventMaterialsPage = React.lazy(() => import("@/pages/public/EventMaterialsPage"));
const EventCheckInPage = React.lazy(() => import("@/pages/public/EventCheckInPage"));
const EventQuestionsPage = React.lazy(() => import("@/pages/public/EventQuestionsPage"));

const AllContactsPage = React.lazy(() => import("@/pages/AllContactsPage"));

// Collaboration Pages
const CollaborationPromptsPage = React.lazy(() => import("@/pages/CollaborationPromptsPage"));
const NewPromptPage = React.lazy(() => import("@/pages/NewPromptPage"));
const EditPromptPage = React.lazy(() => import("@/pages/EditPromptPage"));
const CollaborationsSettingsPage = React.lazy(() => import("@/pages/CollaborationsSettingsPage"));
const CollaborationsEmailAnalyticsPage = React.lazy(() => import("@/pages/CollaborationsEmailAnalyticsPage"));

// Newsroom Pages
const NewsroomDashboard = React.lazy(() => import("@/pages/newsroom/NewsroomDashboard"));

// CLA (Competitive Landscape Analysis) Pages
const CLADashboard = React.lazy(() => import("@/pages/cla/CLADashboard"));
const CLAProjectDetail = React.lazy(() => import("@/pages/cla/CLAProjectDetail"));
const CLAProjectLogs = React.lazy(() => import("@/pages/cla/CLAProjectLogs"));
const CLAPrompts = React.lazy(() => import("@/pages/cla/CLAPrompts"));
const CLASettings = React.lazy(() => import("@/pages/cla/CLASettings"));
const NewsroomSources = React.lazy(() => import("@/pages/newsroom/NewsroomSources"));
const NewsroomCollections = React.lazy(() => import("@/pages/newsroom/NewsroomCollections"));
const NewsroomPrompts = React.lazy(() => import("@/pages/newsroom/NewsroomPrompts"));
const NewsroomBulletins = React.lazy(() => import("@/pages/newsroom/NewsroomBulletins"));
const NewsroomSettings = React.lazy(() => import("@/pages/newsroom/NewsroomSettings"));
const NewsroomPrograms = React.lazy(() => import("@/pages/newsroom/NewsroomPrograms"));
const NewsroomProgramEdit = React.lazy(() => import("@/pages/newsroom/NewsroomProgramEdit"));
const NewsroomProgramLogs = React.lazy(() => import("@/pages/newsroom/NewsroomProgramLogs"));
const NewsroomSponsorships = React.lazy(() => import("@/pages/newsroom/NewsroomSponsorships"));
const NewsroomSponsorshipEdit = React.lazy(() => import("@/pages/newsroom/NewsroomSponsorshipEdit"));
const NewsroomArticleSelection = React.lazy(() => import("@/pages/newsroom/NewsroomArticleSelection"));
const NewsroomArticles = React.lazy(() => import("@/pages/newsroom/NewsroomArticles"));
const HeyGenApiTokensPage = React.lazy(() => import("@/pages/HeyGenApiTokensPage"));
const DistributionLists = React.lazy(() => import("@/pages/newsroom/DistributionLists"));
const DistributionListDetail = React.lazy(() => import("@/pages/newsroom/DistributionListDetail"));
const PressReleaseDispatch = React.lazy(() => import("@/pages/newsroom/PressReleaseDispatch"));
const PressReleaseDispatchCenter = React.lazy(() => import("@/pages/newsroom/PressReleaseDispatchCenter"));
const PressReleaseEmailAnalyticsPage = React.lazy(() => import("@/pages/newsroom/PressReleaseEmailAnalyticsPage"));
const PressReleaseEmailSettingsPage = React.lazy(() => import("@/pages/newsroom/PressReleaseEmailSettingsPage"));
const PressReleaseCampaignsPage = React.lazy(() => import("@/pages/newsroom/PressReleaseCampaignsPage"));
const PressReleaseCampaignDetailPage = React.lazy(() => import("@/pages/newsroom/PressReleaseCampaignDetailPage"));
const PressReleaseValidationPage = React.lazy(() => import("@/pages/newsroom/PressReleaseValidationPage"));

// Avatar Announcements Pages
const AvatarAnnouncements = React.lazy(() => import("@/pages/AvatarAnnouncements"));
const AvatarAnnouncementEditor = React.lazy(() => import("@/pages/AvatarAnnouncementEditor"));

// Prompt Store Pages
const PromptStorePage = React.lazy(() => import("@/pages/PromptStorePage"));
const PromptStoreExamplesPage = React.lazy(() => import("@/pages/PromptStoreExamplesPage"));

// Copywriting Instructions Page
const CopywritingInstructionsPage = React.lazy(() => import("@/pages/CopywritingInstructionsPage"));

// Dynamic Content Library Page
const DynamicContentLibraryPage = React.lazy(() => import("@/pages/DynamicContentLibraryPage"));

// Super Admin Tags & Industries Management
const TagsManagement = React.lazy(() => import("@/pages/super-admin/TagsManagement"));
const IndustriesManagement = React.lazy(() => import("@/pages/super-admin/IndustriesManagement"));
const SuperAdminBrandFeatures = React.lazy(() => import("@/pages/super-admin/SuperAdminBrandFeatures"));
const ImageToPromptGenerator = React.lazy(() => import("@/pages/super-admin/ImageToPromptGenerator"));

// Custom Offers Pages
const CustomOffersManagement = React.lazy(() => import("@/pages/custom-offers/CustomOffersManagement"));
const PublicOfferLandingPage = React.lazy(() => import("@/pages/custom-offers/PublicOfferLandingPage"));

// Addon Management Pages
const AddonDefinitions = React.lazy(() => import("@/pages/super-admin/AddonDefinitions"));
const AddonGrants = React.lazy(() => import("@/pages/super-admin/AddonGrants"));

// AI Creative Models Page
const AICreativeModelsPage = React.lazy(() => import("@/pages/AICreativeModelsPage"));

// AI Models Config Page
const AIModelsConfigPage = React.lazy(() => import("@/pages/AIModelsConfigPage"));

// LLM Source Configuration Page
const LLMConfigPage = React.lazy(() => import("@/pages/LLMConfigPage"));

// Visuals Creation Page (Drawer-based)
const VisualsCreationPage = React.lazy(() => import("@/pages/VisualsCreationPage"));

// Voice Waveform Test Page
const VoiceWaveformTestPage = React.lazy(() => import("@/pages/VoiceWaveformTestPage"));

// Business Metrics Feed Page
const BusinessMetricsFeedPage = React.lazy(() => import("@/pages/BusinessMetricsFeedPage"));

// Leads Widget Embed Page
const LeadsWidgetEmbedPage = React.lazy(() => import("@/pages/LeadsWidgetEmbedPage"));

// Public Brand Embed Pages
const PublicChatEmbedPage = React.lazy(() => import("@/pages/PublicChatEmbedPage"));
const PublicVoiceEmbedPage = React.lazy(() => import("@/pages/PublicVoiceEmbedPage"));

// Public Widget Config Pages
const PublicChatsPage = React.lazy(() => import("@/pages/PublicChatsPage"));
const PublicChatWidgetConfigPage = React.lazy(() => import("@/pages/PublicChatWidgetConfigPage"));
const PublicChatKnowledgePage = React.lazy(() => import("@/pages/PublicChatKnowledgePage"));
const PublicVoiceWidgetConfigPage = React.lazy(() => import("@/pages/PublicVoiceWidgetConfigPage"));

// Content Approval Dashboard Page
const ContentApprovalDashboard = React.lazy(() => import("@/pages/ContentApprovalDashboard"));

// Route Metadata Manager Page
const RouteMetadataManager = React.lazy(() => import("@/pages/admin/RouteMetadataManager"));

// MCP OAuth Clients Page
const McpOAuthClients = React.lazy(() => import("@/pages/admin/McpOAuthClients"));

// Post Detail Page  
const PostDetailPage = React.lazy(() => import("@/pages/PostDetailPage"));

// Blog Post Detail Page
const BlogPostDetailPage = React.lazy(() => import("@/pages/BlogPostDetailPage"));

// FTE Page
const FtePage = React.lazy(() => import("@/pages/FtePage"));

// Sales Operations Pages
const SalesMomsPage = React.lazy(() => import("@/pages/SalesOperations/SalesMoms"));
const SalesPromptsPage = React.lazy(() => import("@/pages/SalesOperations/SalesPrompts"));
const AITrainingFeedbackPage = React.lazy(() => import("@/pages/SalesOperations/AITrainingFeedback"));
const SalesProposalsPage = React.lazy(() => import("@/pages/SalesOperations/SalesProposals"));
const ProposalBuilderPage = React.lazy(() => import("@/pages/SalesOperations/ProposalBuilder"));
const ProposalInsightsPage = React.lazy(() => import("@/pages/SalesOperations/ProposalInsights"));
const MSATemplatesPage = React.lazy(() => import("@/pages/SalesOperations/MSATemplates"));
const MSADocumentsPage = React.lazy(() => import("@/pages/SalesOperations/MSADocuments"));
const MSAEditorPage = React.lazy(() => import("@/pages/SalesOperations/MSAEditor"));
const MSADocumentEditorPage = React.lazy(() => import("@/pages/SalesOperations/MSADocumentEditor"));
const SupportiveTemplatesPage = React.lazy(() => import("@/pages/SalesOperations/SupportiveTemplates"));
const SupportiveDocumentsPage = React.lazy(() => import("@/pages/SalesOperations/SupportiveDocuments"));
const SupportiveEditorPage = React.lazy(() => import("@/pages/SalesOperations/SupportiveEditor"));
const SupportiveDocumentEditorPage = React.lazy(() => import("@/pages/SalesOperations/SupportiveDocumentEditor"));
const SalesInsightsPage = React.lazy(() => import("@/pages/SalesOperations/SalesInsights"));

// Proposal Portal Pages
const ProposalPortalAuthPage = React.lazy(() => import("@/pages/ProposalPortal/ProposalPortalAuthPage"));
const ProposalPortalViewPage = React.lazy(() => import("@/pages/ProposalPortal/ProposalPortalViewPage"));

// CRO Pages
const CroDashboard = React.lazy(() => import("@/pages/cro/CroDashboard"));
const WebsiteSuggestions = React.lazy(() => import("@/pages/cro/WebsiteSuggestions"));
const CroApiTokensPage = React.lazy(() => import("@/pages/CroApiTokensPage"));
const AccountingApiTokensPage = React.lazy(() => import("@/pages/AccountingApiTokens"));
const DomainsApiTokensPage = React.lazy(() => import("@/pages/DomainsApiTokens"));
const DomainAvailabilityPage = React.lazy(() => import("@/pages/DomainAvailabilityPage"));

// Documentation Hub Pages
const DocumentationHubDashboardPage = React.lazy(() => import("@/pages/DocumentationHubDashboardPage"));
const DocumentationHubEventsPage = React.lazy(() => import("@/pages/DocumentationHubEventsPage"));
const DocumentationHubDocumentsPage = React.lazy(() => import("@/pages/DocumentationHubDocumentsPage"));
const DocumentationHubTipsPage = React.lazy(() => import("@/pages/DocumentationHubTipsPage"));
const DocumentationHubSnippetsPage = React.lazy(() => import("@/pages/DocumentationHubSnippetsPage"));

// Business on Map Page
const BusinessOnMapPage = React.lazy(() => import("@/pages/BusinessOnMapPage"));

// Keyword Regex Filter Page
const KeywordRegexFilterPage = React.lazy(() => import("@/pages/KeywordRegexFilterPage"));

// PPTs/PDFs API Tokens Page
const PptsApiTokensPage = React.lazy(() => import("@/pages/PptsApiTokensPage"));

// API Tokens Ownership Page
const ApiTokensOwnershipPage = React.lazy(() => import("@/pages/ApiTokensOwnershipPage"));

// Role Permissions Page
const RolePermissionsPage = React.lazy(() => import("@/pages/RolePermissions"));

// Racing Bar Charts Page
const RacingBarCharts = React.lazy(() => import("@/pages/RacingBarCharts"));

// Human Resources Job Posts Pages
const JobPostsPage = React.lazy(() => import("@/pages/human-resources/JobPostsPage"));
const JobPostFormPage = React.lazy(() => import("@/pages/human-resources/JobPostFormPage"));
const JobPostsEmbedGuide = React.lazy(() => import("@/pages/human-resources/JobPostsEmbedGuide"));

// Human Resources Applicants Pages
const ApplicantsPage = React.lazy(() => import("@/pages/human-resources/ApplicantsPage"));
const ApplicantDetailPage = React.lazy(() => import("@/pages/human-resources/ApplicantDetailPage"));
const ApplicantPromptsPage = React.lazy(() => import("@/pages/human-resources/ApplicantPromptsPage"));

// Public Job Application Page
const JobApplicationPage = React.lazy(() => import("@/pages/public/JobApplicationPage"));

export { MASTER_ROUTES as routes };

export const AppRoutes = () => {
  return (
    <Routes>
      {/* Super Admin Routes - MUST BE FIRST to avoid conflicts */}
      <Route
        path="/super-admin"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SuperAdminDashboard />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/users"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SuperAdminUsers />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/brands"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SuperAdminBrands />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/analytics"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SuperAdminAnalytics />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/settings"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SuperAdminSettings />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/pin-protection"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PinProtectionSettings />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/google-annotations"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <GoogleAnnotationsPage />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/chat-logs"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SuperAdminChatLogs />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/provisioning-services"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ProvisioningServices />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/reporting-dashboard-config"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReportingDashboardConfigPage />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/activity"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SuperAdminActivity />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/theme-management"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ThemeManagement />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/quota-management"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <QuotaManagementPage />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/tags-management"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <TagsManagement />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/industries-management"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <IndustriesManagement />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/brand-features"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SuperAdminBrandFeatures />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/image-to-prompt"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ImageToPromptGenerator />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/custom-subscription-offers"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CustomOffersManagement />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/addon-definitions"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AddonDefinitions />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      <Route
        path="/super-admin/addon-grants"
        element={
          <SuperAdminRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AddonGrants />
            </React.Suspense>
          </SuperAdminRoute>
        }
      />
      
      {/* Public Offer Landing Page */}
      <Route
        path="/offer/:offerCode"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <PublicOfferLandingPage />
          </React.Suspense>
        }
      />
      
      
      {/* Services Provisioned Route */}
      <Route
        path="/services-provisioned"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ServicesProvisioned />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Apify Usage Monitor Route */}
      <Route
        path="/admin/apify-usage"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ApifyUsagePage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* User Activity Tracking Route */}
      <Route
        path="/admin/user-activity-tracking"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <UserActivityTrackingPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* User Time Tracking Route */}
      <Route
        path="/admin/user-time-tracking"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <UserTimeTrackingPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Growth Standup Admin Route */}
      <Route
        path="/admin/growth-standup"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <GrowthStandupAdminPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/consultant-applications"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ConsultantApplicationsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      {/* Team Cards Route */}
      <Route
        path="/admin/team-cards"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <TeamCardsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Voice Assistant Tools Route */}
      <Route
        path="/admin/voice-assistant-tools"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <VoiceAssistantToolsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* MCP Server Management Route */}
      <Route
        path="/admin/mcp-server"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <McpServerManagement />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      {/* MCP Server Documentation Route */}
      <Route
        path="/docs/mcp-server"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <McpServerDocs />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/link-building/email-templates"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <EmailTemplatesPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/link-building/email-history"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <EmailSendsHistoryPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/link-building/email-settings"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <EmailAutomationSettingsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Racing Bar Charts Route */}
      <Route
        path="/racing-bar-chart"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <RacingBarCharts />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Plans and Billing Routes */}
      <Route
        path="/plans-and-billing"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PlansAndBillingPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans-and-billing/usage"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PlansAndBillingUsagePage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Public routes */}
      <Route path="/" element={<Index />} />
      <Route path="/home" element={
        <React.Suspense fallback={<LoadingFallback />}>
          <HomePage />
        </React.Suspense>
      } />
      <Route path="/consultant-application" element={
        <React.Suspense fallback={<LoadingFallback />}>
          <ConsultantApplicationPage />
        </React.Suspense>
      } />
      
      {/* Public Quiz Route */}
      <Route path="/quiz/:shareCode" element={
        <React.Suspense fallback={<LoadingFallback />}>
          <PublicQuizPage />
        </React.Suspense>
      } />
      
      {/* Public Mini Game Route */}
      <Route path="/mini-game/:shareCode" element={
        <React.Suspense fallback={<LoadingFallback />}>
          <PublicMiniGamePage />
        </React.Suspense>
      } />
      
      {/* Public Poll Routes */}
      <Route path="/poll/:shareCode" element={
        <React.Suspense fallback={<LoadingFallback />}>
          <PollTakerPage />
        </React.Suspense>
      } />
      
      <Route path="/poll/:shareCode/leaderboard" element={
        <React.Suspense fallback={<LoadingFallback />}>
          <PollLeaderboardPublicPage />
        </React.Suspense>
      } />
      
      {/* Public Survey Route */}
      <Route path="/survey/:token" element={
        <React.Suspense fallback={<LoadingFallback />}>
          <SurveyTakerPage />
        </React.Suspense>
      } />
      
      <Route path="/Check-your-SEO-Data" element={
        <React.Suspense fallback={<LoadingFallback />}>
          <SeoDataCheckPage />
        </React.Suspense>
      } />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/trial-expired" element={
        <React.Suspense fallback={<LoadingFallback />}>
          <TrialExpiredPage />
        </React.Suspense>
      } />
      <Route path="/google-auth-callback" element={<GoogleAuthCallbackPage />} />
      
      {/* Test Navigation Preferences Route */}
      <Route
        path="/test-nav-preferences"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <TestNavigationPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Experiments Index Page */}
      <Route
        path="/experiments"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ExperimentsIndexPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Document to Page Builder Experiment */}
      <Route
        path="/experiments/document-to-page"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DocumentToPageBuilderPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Data Orchestrator Experiment */}
      <Route
        path="/experiments/data-orchestrator"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DataOrchestratorPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Image Coordinates Experiment */}
      <Route
        path="/experiments/image-coordinates"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ImageCoordinatesExperimentPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Collaborative Editor Experiment */}
      <Route
        path="/experiments/collaborative-editor"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CollaborativeEditorExperiment />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Dashboard Builder Experiment */}
      <Route
        path="/experiments/dashboards"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DashboardsExperimentPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* OSDE Form Experiment */}
      <Route
        path="/experiment/osde"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <OSDEPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/experiment/osde/data"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <OSDEDataReportPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Email Preview Route */}
      <Route
        path="/email-preview"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <EmailPreviewPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Email Designer Route */}
      <Route
        path="/email-designer"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <EmailDesignerPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Voice Waveform Test Route */}
      <Route
        path="/voice-waveform-test"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <VoiceWaveformTestPage />
          </React.Suspense>
        }
      />
      
      {/* Client Portal Routes */}
      <Route
        path="/client/:token"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ClientPortalAuthPage />
          </React.Suspense>
        }
      />
      <Route
        path="/client/:token/dashboard"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ClientPortalDashboard />
          </React.Suspense>
        }
      />
      <Route
        path="/client/:token/leads"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ClientPortalLeadsPage />
          </React.Suspense>
        }
      />
      <Route
        path="/client/:token/comments"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ClientPortalComments />
          </React.Suspense>
        }
      />
      <Route
        path="/client/:token/saio-dashboard"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ClientPortalSaioDashboard />
          </React.Suspense>
        }
      />
      <Route
        path="/client/:token/google-ads"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ClientPortalGoogleAdsPage />
          </React.Suspense>
        }
      />
      <Route
        path="/client/:token/analytics"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ClientPortalAnalyticsPage />
          </React.Suspense>
        }
      />
      
      {/* Client Portal Business Activity Route */}
      <Route
        path="/client/:token/business-activity"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ClientPortalBusinessActivityPage />
          </React.Suspense>
        }
      />
      
      {/* Google Ads Analytics Routes */}
      <Route 
        path="/googleads/keywords/:brandId?" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <GoogleAdsKeywordsPage />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/googleads/search-terms/:brandId?" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <GoogleAdsSearchTermsPage />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* SOP Task Assignment Routes */}
      <Route
        path="/sops/area/:areaId/assign-tasks"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SopAreaTaskAssignmentPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sops/taskgroup/:taskGroupId/assign-tasks"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SopTaskGroupTaskAssignmentPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* OS Roadmap Route */}
      <Route
        path="/os-roadmap"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <OSRoadmapPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Release Notes Route */}
      <Route
        path="/os-release-notes"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReleaseNotesPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Add Release Notes Admin Route */}
      <Route
        path="/adding-os-release-notes"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AddReleaseNotesPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Edit Release Notes Admin Route */}
      <Route
        path="/editing-os-release-notes/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <EditReleaseNotesPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Route Metadata Manager Admin Route */}
      <Route
        path="/admin/route-metadata"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <RouteMetadataManager />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* MCP OAuth Clients Admin Route */}
      <Route
        path="/admin/mcp-oauth-clients"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <McpOAuthClients />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO Test Results Route */}
      <Route
        path="/seo-testing/:testId/results"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoTestResultsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO Internal Linking Analysis Routes */}
      <Route
        path="/seo/internal-linking"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <InternalLinkingAnalysis />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seo/internal-linking/crawled-pages"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <InternalLinkingCrawledPages />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seo/internal-linking/tokens"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <InternalLinkingTokens />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seo/internal-linking/enriched-keywords"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <InternalLinkingEnrichedKeywords />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seo/internal-linking/opportunities"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <InternalLinkingOpportunities />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seo-vocabulary"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoVocabulary />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO AI Insights Route */}
      <Route
        path="/seo/ai-insights"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoAiInsightsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO Pages Route */}
      <Route
        path="/seo/pages"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoPagesPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO Cannibalization Route */}
      <Route
        path="/seo/cannibalization"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoCannibalizationPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO Content Intelligence Route */}
      <Route
        path="/seo/content-intelligence"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoContentIntelligencePage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO Analytics Dashboard Route */}
      <Route
        path="/seo/analytics"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoAnalyticsDashboardPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO Keywords Route */}
      <Route
        path="/seo/keywords"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoKeywordsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO Keyword Clusters Route */}
      <Route
        path="/seo/keyword-clusters"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoKeywordClustersPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO Page Speed Route */}
      <Route
        path="/seo/pagespeed"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoPageSpeedPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO Content Briefs Route */}
      <Route
        path="/seo/briefs"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoContentBriefsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* SEO Backlink Intelligence Route */}
      <Route
        path="/seo/backlink-intelligence"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoBacklinkIntelligencePage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Metrics Aggregations Route */}
      <Route
        path="/metrics-aggregations"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <MetricsAggregationsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Metrics Configuration Route */}
      <Route
        path="/metrics-configuration"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <MetricsConfigurationPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Metrics Definitions Route */}
      <Route
        path="/admin/metrics-definitions"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <MetricsDefinitionsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Metrics Cockpit Route */}
      <Route
        path="/admin/metrics-cockpit"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <MetricsCockpitPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Mobile Footer Config Route */}
      <Route
        path="/mobile-footer-config"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <MobileFooterConfigPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Business Activity Route */}
      <Route
        path="/business-activity"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <BusinessActivityPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* YouTube SEO Suggestions Results Route */}
      <Route
        path="/youtube-seo-suggestions-results"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <YouTubeSEOSuggestionsResultsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Instagram Data Collector Route */}
      <Route
        path="/instagram-data-collector"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <InstagramDataCollectorPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Prompt Store Routes */}
      <Route
        path="/prompt-store"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <PromptStorePage />
          </React.Suspense>
        }
      />
      <Route
        path="/prompt-store/:type/:itemId/examples"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <PromptStoreExamplesPage />
          </React.Suspense>
        }
      />
      <Route 
        path="/prompt-store/embed-guide" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PromptsEmbedGuide />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* Copywriting Instructions Route */}
      <Route
        path="/copywriting-instructions"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CopywritingInstructionsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* AI Creative Models Route */}
      <Route
        path="/ai-creative-models"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AICreativeModelsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* AI Models Config Route */}
      <Route
        path="/ai-models-config"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AIModelsConfigPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* LLM Source Configuration Route */}
      <Route
        path="/admin/llm-config"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LLMConfigPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Dynamic Content Library Route */}
      <Route
        path="/dynamic-content-library"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DynamicContentLibraryPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* FTE Route */}
      <Route
        path="/fte"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <FtePage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Visual Creation Route */}
      <Route
        path="/visuals-creation"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <VisualsCreationPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Public Widget Config Routes */}
      <Route
        path="/public-chats"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PublicChatsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/public-chat-widget-config"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PublicChatWidgetConfigPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/public-chat-knowledge"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PublicChatKnowledgePage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/public-voice-widget-config"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PublicVoiceWidgetConfigPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* PPTs/PDFs API Tokens Route */}
      <Route
        path="/ppts-api-tokens"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PptsApiTokensPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Protected routes from MASTER_ROUTES */}
      {MASTER_ROUTES.filter(route => route.requiresAuth).map((route, index) => {
        const LazyComponent = route.component;
        const element = (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyComponent />
          </React.Suspense>
        );

        return (
          <Route
            key={`${route.path}-${index}`}
            path={route.path}
            element={<ProtectedRoute>{element}</ProtectedRoute>}
          />
        );
      })}

      {/* Public routes from MASTER_ROUTES */}
      {MASTER_ROUTES.filter(route => !route.requiresAuth).map((route, index) => {
        const LazyComponent = route.component;
        const element = (
          <React.Suspense fallback={<LoadingFallback />}>
            <LazyComponent />
          </React.Suspense>
        );

        return (
          <Route
            key={`public-${route.path}-${index}`}
            path={route.path}
            element={element}
          />
        );
      })}
      
      {/* Post Detail Route */}
      <Route
        path="/posts/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PostDetailPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Blog Post Detail Route */}
      <Route
        path="/blogposts/:slug"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <BlogPostDetailPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Content Approval Dashboard Route */}
      <Route
        path="/content-approval-dashboard"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ContentApprovalDashboard />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Sales Operations Routes */}
      <Route
        path="/sales-operations/moms"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SalesMomsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/prompts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SalesPromptsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/ai-training-feedback"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AITrainingFeedbackPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/proposals"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SalesProposalsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/proposals/new"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ProposalBuilderPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/proposals/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ProposalBuilderPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/proposals/:id/insights"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ProposalInsightsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/msa-templates"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <MSATemplatesPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/msa-documents"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <MSADocumentsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/msa-editor/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <MSAEditorPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/msa-document-editor/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <MSADocumentEditorPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/supportive-templates"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SupportiveTemplatesPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/supportive-documents"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SupportiveDocumentsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/supportive-editor/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SupportiveEditorPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-operations/supportive-document-editor/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SupportiveDocumentEditorPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-insights"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SalesInsightsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Public Proposal Portal Routes */}
      <Route
        path="/proposal/:shareCode"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ProposalPortalAuthPage />
          </React.Suspense>
        }
      />
      <Route
        path="/proposal/:shareCode/view"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ProposalPortalViewPage />
          </React.Suspense>
        }
      />

      {/* Business Metrics Feed Route */}
      <Route
        path="/business-metrics-feed"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <BusinessMetricsFeedPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Scale 2Y3X Routes */}
      <Route
        path="/scale-2y3x/dashboard"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/Scale2Y3XDashboard")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scale-2y3x/growth-lab-team"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/GrowthLabTeamPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scale-2y3x/sprints"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/SprintsPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sprints/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/SprintDetailPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scale-2y3x/goals"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/GoalsPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scale-2y3x/ideas"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/IdeasPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scale-2y3x/current-quarter-tasks"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/CurrentQuarterTasksPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scale-2y3x/tasks"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/TasksPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scale-2y3x/quarterly-tasks"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/QuarterlyTasksPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scale-2y3x/2y3x-builder"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/Strategy2Y3XBuilderPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scale-2y3x/about"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/About2Y3XPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scale-2y3x/phases"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/PhasesPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scale-2y3x/performance-insights"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/scale-2y3x/PerformanceInsightsPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* CRO Routes */}
      <Route
        path="/cro"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CroDashboard />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cro/prompts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              {React.createElement(React.lazy(() => import("@/pages/cro/CroPromptsPage")))}
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cro/website-suggestions"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <WebsiteSuggestions />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
<Route
        path="/cro/api-tokens"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CroApiTokensPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounting-api-tokens"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AccountingApiTokensPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/domains-api-tokens"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DomainsApiTokensPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/domain-availability"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DomainAvailabilityPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Naming Routes */}
      <Route
        path="/naming"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NamingPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/naming/prompts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NamingPromptsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Newsroom Routes */}
      <Route
        path="/newsroom"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomDashboard />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/sources"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomSources />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/collections"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomCollections />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/prompts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomPrompts />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/bulletins"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomBulletins />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/settings"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomSettings />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/programs"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomPrograms />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/programs/new"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomProgramEdit />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/programs/:id/edit"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomProgramEdit />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/programs/:programId/logs"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomProgramLogs />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/programs/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomProgramEdit />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/sponsorships"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomSponsorships />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/sponsorships/new"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomSponsorshipEdit />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/sponsorships/:id/edit"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomSponsorshipEdit />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/articles/:collectionId"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomArticles />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/collections/:collectionId/select"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewsroomArticleSelection />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/api-tokens"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <HeyGenApiTokensPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/press-releases/email-analytics"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PressReleaseEmailAnalyticsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/press-releases/email-settings"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PressReleaseEmailSettingsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/press-releases/campaigns"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PressReleaseCampaignsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/press-releases/campaigns/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PressReleaseCampaignDetailPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/distribution-lists"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DistributionLists />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/distribution-lists/:listId"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DistributionListDetail />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/press-releases/dispatch/:pressReleaseId"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PressReleaseDispatch />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/press-releases/validate/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PressReleaseValidationPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/newsroom/dispatch-center"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PressReleaseDispatchCenter />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* CLA (Competitive Landscape Analysis) Routes */}
      <Route
        path="/cla"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CLADashboard />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cla/:id"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CLAProjectDetail />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cla/projects/:projectId/logs"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CLAProjectLogs />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cla/prompts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CLAPrompts />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cla/settings"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CLASettings />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Avatar Announcements Routes */}
      <Route
        path="/avatar-announcements"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AvatarAnnouncements />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/avatar-announcements/new"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AvatarAnnouncementEditor />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/avatar-announcements/:id/edit"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AvatarAnnouncementEditor />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Badges Routes */}
      {/* Badge Claim Route - Must be BEFORE other badge routes to match correctly */}
      <Route
        path="/badges/claim/:badgeId"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <BadgeClaimPage />
          </React.Suspense>
        }
      />
      <Route
        path="/badges"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <Badges />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/badges/new"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <BadgeEditor />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/badges/:id/edit"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <BadgeEditor />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/badges/users"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <BadgeUsers />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/badges/analytics"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <BadgeAnalytics />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Cold Outreach Routes */}
      <Route
        path="/cold-outreach/campaigns"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachCampaignsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/campaigns/:campaignId"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachCampaignDetailPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/campaigns/:campaignId/flow"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachFlowViewPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/campaigns/:campaignId/email-settings"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachEmailSettingsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/create-campaign"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachCreateCampaignPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/lists"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachListsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/lists/:listId"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachListDetailPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/prompts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachPromptsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/collaborations/prompts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CollaborationPromptsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/collaborations/prompts/new"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <NewPromptPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/collaborations/prompts/:id/edit"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <EditPromptPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/collaborations/settings"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CollaborationsSettingsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/collaborations/email-analytics"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CollaborationsEmailAnalyticsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/contacts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachContactsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AllContactsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/dispatch"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachDispatchPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/analytics"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachAnalyticsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/email-analytics"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachEmailAnalyticsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cold-outreach/email-settings"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachCentralEmailSettingsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-providers"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ColdOutreachEmailProvidersPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Referral System Routes */}
      <Route
        path="/referral/contacts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralContactsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/programs"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralProgramsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/programs/:programId"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralProgramDetailPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/campaigns"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralCampaignsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/campaigns/create"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralCreateCampaignPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/campaigns/:campaignId/emails"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralCampaignEmailsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/campaigns/:campaignId"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralCampaignDetailPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/dispatch"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralDispatchPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/prompts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralPromptsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral-system/email-settings"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralEmailSettingsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/analytics"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralAnalyticsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/email-analytics"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralEmailAnalyticsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/join"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ReferralJoinPage />
          </React.Suspense>
        }
      />
      <Route
        path="/join/:programSlug/:ref"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ReferralJoinPage />
          </React.Suspense>
        }
      />
      <Route
        path="/referral-control-center"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ReferralControlCenterPage />
          </React.Suspense>
        }
      />
      <Route
        path="/event-materials/:brandSlug/:eventSlug"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <EventMaterialsPage />
          </React.Suspense>
        }
      />
      <Route
        path="/event-check-in/:brandSlug/:eventSlug"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <EventCheckInPage />
          </React.Suspense>
        }
      />
      <Route
        path="/event-questions/:brandSlug/:eventSlug"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <EventQuestionsPage />
          </React.Suspense>
        }
      />
      <Route
        path="/referral/lists"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralListsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral/lists/:listId"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralListDetailPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral-participants-feed"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ReferralParticipantsFeedPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Business on Map Route */}
      <Route
        path="/business-on-map"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <BusinessOnMapPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Documentation Hub Routes */}
      <Route
        path="/documentation-hub/dashboard"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DocumentationHubDashboardPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentation-hub/events"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DocumentationHubEventsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentation-hub/documents"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DocumentationHubDocumentsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentation-hub/tips"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DocumentationHubTipsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentation-hub/snippets"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DocumentationHubSnippetsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Public Embed Routes - For external embedding (no auth required) */}
      <Route
        path="/embed/leads-widget"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <LeadsWidgetEmbedPage />
          </React.Suspense>
        }
      />
      <Route
        path="/embed/referral-participants"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ReferralParticipantsEmbedPage />
          </React.Suspense>
        }
      />
      <Route
        path="/embed/referral-leaderboard"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ReferralLeaderboardEmbedPage />
          </React.Suspense>
        }
      />
      <Route
        path="/embed/referral-share"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ReferralShareEmbedPage />
          </React.Suspense>
        }
      />
      <Route
        path="/embed/referral-join"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ReferralJoinEmbedPage />
          </React.Suspense>
        }
      />
      
      {/* Public Chat Widget Routes */}
      <Route
        path="/embed/public-chat"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <PublicChatEmbedPage />
          </React.Suspense>
        }
      />
      
      <Route
        path="/embed/public-chat-fullsize"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <PublicChatEmbedPage />
          </React.Suspense>
        }
      />

      {/* Main public widget route */}
      <Route
        path="/public-chat-widget"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <PublicChatEmbedPage />
          </React.Suspense>
        }
      />
      <Route
        path="/embed/public-voice"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <PublicVoiceEmbedPage />
          </React.Suspense>
        }
      />
      
      {/* Support Dashboard (Admin) */}
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SupportDashboard />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Canned Responses (Admin) */}
      <Route
        path="/support/canned-responses"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CannedResponsesPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Keyword Regex Filter */}
      <Route
        path="/keyword-regex-filter"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <KeywordRegexFilterPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Fact Checker Routes */}
      <Route
        path="/fact-checker"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <FactCheckerDashboard />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-fact-checks"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <MyFactChecks />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fact-checker/session/:sessionId"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <FactCheckResults />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fact-checker/prompts"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <FactCheckPromptsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Sellable Products Routes */}
      <Route
        path="/sellable-products/my-store"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <MyStore />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sellable-products/payment-methods"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PaymentMethods />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sellable-products/default-paths"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LMSDefaultPathsPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lms/permalinks"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LMSPermalinksPage />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments/public-api"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PublicPaymentApiGuide />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Public Course Marketplace Routes */}
      <Route path="/courses" element={<React.Suspense fallback={<LoadingFallback />}><PublicCourseCatalog /></React.Suspense>} />
      <Route path="/courses/embed" element={<React.Suspense fallback={<LoadingFallback />}><PublicCoursesEmbed /></React.Suspense>} />
      <Route path="/embed/events" element={<React.Suspense fallback={<LoadingFallback />}><PublicEventsEmbed /></React.Suspense>} />
      <Route path="/embed/event" element={<React.Suspense fallback={<LoadingFallback />}><PublicEventEmbed /></React.Suspense>} />
      <Route 
        path="/courses/embed-guide" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CoursesEmbedGuide />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/instructors/embed-guide" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <InstructorsEmbedGuide />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/academies/embed-guide" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <AcademiesEmbedGuide />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      <Route path="/courses/:courseId" element={<React.Suspense fallback={<LoadingFallback />}><PublicCourseDetail /></React.Suspense>} />
      <Route path="/courses/checkout/:courseId" element={<React.Suspense fallback={<LoadingFallback />}><CourseCheckout /></React.Suspense>} />
      <Route path="/courses/payment/success" element={<React.Suspense fallback={<LoadingFallback />}><CoursePaymentSuccess /></React.Suspense>} />
      <Route path="/courses/payment/cancel" element={<React.Suspense fallback={<LoadingFallback />}><CoursePaymentCancel /></React.Suspense>} />
      <Route path="/grhotels-checkout" element={<React.Suspense fallback={<LoadingFallback />}><GRHotelsCheckout /></React.Suspense>} />

      {/* Public Academy Routes */}
      <Route path="/academies" element={<React.Suspense fallback={<LoadingFallback />}><PublicAcademyCatalog /></React.Suspense>} />
      <Route path="/academies/:slug" element={<React.Suspense fallback={<LoadingFallback />}><PublicAcademyDetail /></React.Suspense>} />
      <Route path="/academies/:slug/courses" element={<React.Suspense fallback={<LoadingFallback />}><PublicCourseCatalog /></React.Suspense>} />

      {/* LMS Learn Portal Routes (Public) */}
      <Route
        path="/learn/auth"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <LearnAuthPage />
          </React.Suspense>
        }
      />
      <Route
        path="/learn/login"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <LearnLoginPage />
          </React.Suspense>
        }
      />
      <Route
        path="/learn/grhotels-auth"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <GRHotelsAuthPage />
          </React.Suspense>
        }
      />
      <Route
        path="/learn/verify"
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <LearnVerifyPage />
          </React.Suspense>
        }
      />
      
      {/* LMS Learn Portal Routes (Protected) */}
      <Route
        path="/learn/dashboard"
        element={
          <LearnProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnDashboard />
            </React.Suspense>
          </LearnProtectedRoute>
        }
      />
      <Route
        path="/learn/course/:courseId"
        element={
          <LearnProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnCoursePage />
            </React.Suspense>
          </LearnProtectedRoute>
        }
      />
      <Route
        path="/learn/lesson/:lessonId"
        element={
          <LearnProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnLessonPage />
            </React.Suspense>
          </LearnProtectedRoute>
        }
      />
      
      {/* LMS Admin Routes (Protected) */}
      <Route
        path="/lms/dashboard"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminDashboard />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/coursemanagement"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminCourseList />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/coursemanagement/:courseId"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminCourseEdit />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/instructors"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminInstructors />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/course-types"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <CourseTypesPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/analytics"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminAnalytics />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/digcomp"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LmsDigitalCompetencesPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/digcomp/statistics"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DigcompStatisticsPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/digcomp/overview"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DigcompOverviewPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/digcomp/framework"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DigcompFrameworkPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/digcomp/mapping-overview"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DigcompMappingOverviewPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/digcomp/student-progress"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DigcompStudentProgressPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/digcomp/assessments"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DigcompAssessmentsPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/digcomp/badges-certificates"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <DigcompBadgesCertificatesPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/email-analytics"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminEmailAnalytics />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/prompts"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LmsPromptsPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/enrollments"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminEnrollmentsPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/company-enrollments"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminCompanyEnrollmentsPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/webhook-settings"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LMSWebhookSettings />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/leads-webhook-settings"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LMSLeadsWebhookSettings />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/leads"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LMSLeadsPage />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/notification-settings"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LMSNotificationSettings />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/general-settings"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LMSGeneralSettings />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/associations"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminAssociations />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/settlements"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminSettlements />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/settlements/new"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminSettlementsNew />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/academies"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminAcademies />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/course-types"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminCourseTypes />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/learn/admin/associations"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminAssociations />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/stripe-config"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminStripeConfig />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/certificate-templates"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminCertificateTemplates />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/student-eligibility"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminStudentEligibility />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/quiz-management"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminQuizManagement />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/quiz-submissions"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminQuizSubmissions />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/feedback"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminFeedback />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/lms/ask-for-more"
        element={
          <LearnAdminProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnAdminAskForMore />
            </React.Suspense>
          </LearnAdminProtectedRoute>
        }
      />
      <Route
        path="/learn/certificate/:certificateId"
        element={
          <LearnProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnCertificatePage />
            </React.Suspense>
          </LearnProtectedRoute>
        }
      />
      <Route
        path="/learn/verify-certificate"
        element={
          <LearnProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <LearnCertificateVerifyPage />
            </React.Suspense>
          </LearnProtectedRoute>
        }
      />
      
      {/* SiteAudit Routes */}
      {siteauditRoutes.map((route) => {
        const Component = route.component;
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              route.requiresAuth ? (
                <ProtectedRoute>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Component />
                  </React.Suspense>
                </ProtectedRoute>
              ) : (
                <React.Suspense fallback={<LoadingFallback />}>
                  <Component />
                </React.Suspense>
              )
            }
          />
        );
      })}
      
      {/* 360 CRM Routes */}
      {routes360.map((route) => {
        const Component = route.element;
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              <ProtectedRoute>
                <React.Suspense fallback={<LoadingFallback />}>
                  <Component />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
        );
      })}
      
      {/* Personal Cards Routes */}
      {/* Public Personal Card Page */}
      <Route 
        path="/incoming/:recordId" 
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <PersonalCardPublicPage />
          </React.Suspense>
        } 
      />
      
      {/* Admin Personal Cards Management */}
      <Route 
        path="/personal-cards" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PersonalCardsPage />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* PDF Access Routes */}
      {/* Admin PDF Access Settings */}
      <Route 
        path="/pdf-access-settings" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PdfAccessSettings />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* PDF Access Statistics */}
      <Route 
        path="/pdf-access-stats" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <PdfAccessStats />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* URL Shortener */}
      <Route 
        path="/url-shortener" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <UrlShortener />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* URL Shortener Stats */}
      <Route 
        path="/url-shortener/stats" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <UrlShortenerStats />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* Public Short URL Redirect */}
      <Route 
        path="/s/:slug" 
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ShortUrlRedirect />
          </React.Suspense>
        } 
      />
      
      {/* Shared Annotation Report (Public) */}
      <Route 
        path="/shared/annotation-report/:shareCode" 
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <SharedAnnotationReportPage />
          </React.Suspense>
        }
      />

      {/* Shared Custom Report (Public) */}
      <Route 
        path="/shared/custom-report/:shareCode" 
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <SharedCustomReportPage />
          </React.Suspense>
        }
      />
      
      {/* Public PDF Viewer */}
      <Route 
        path="/view-prices" 
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <ViewPrices />
          </React.Suspense>
        } 
      />
      
      {/* Testimonials Routes */}
      {/* Admin Testimonials Management */}
      <Route 
        path="/testimonials" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <TestimonialsPage />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* Testimonials Embed Guide */}
      <Route 
        path="/testimonials/embed-guide" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <TestimonialsEmbedGuide />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* Testimonials Webhook Guide */}
      <Route 
        path="/testimonials/webhook-guide" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <TestimonialsWebhookGuide />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* Public Testimonial Submission Form */}
      <Route 
        path="/submit-testimonial" 
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <SubmitTestimonialPage />
          </React.Suspense>
        }
      />
      
      {/* Public Testimonials Embed */}
      <Route 
        path="/testimonials/embed" 
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <PublicTestimonialsEmbed />
          </React.Suspense>
        } 
      />
      
      {/* SEO Revamb Routes */}
      <Route 
        path="/seo-revamb" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoRevambPage />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/seo-revamb/:auditId" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <SeoRevambAuditDetailPage />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* Client Logos Routes */}
      {/* Admin Client Logos Management */}
      <Route
        path="/client-logos" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ClientLogosPage />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* Client Logos Embed Guide */}
      <Route 
        path="/client-logos/embed-guide" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ClientLogosEmbedGuide />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* Products Embed Guide */}
      <Route 
        path="/brand-products/embed-guide" 
        element={
          <ProtectedRoute>
            <React.Suspense fallback={<LoadingFallback />}>
              <ProductsEmbedGuide />
            </React.Suspense>
          </ProtectedRoute>
        } 
      />
      
      {/* Public Client Logos Embed */}
      <Route 
        path="/client-logos/embed" 
        element={
          <React.Suspense fallback={<LoadingFallback />}>
            <PublicClientLogosEmbed />
          </React.Suspense>
        } 
      />
      
      {/* Human Resources Routes */}
      <Route path="/human-resources/employees" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><HREmployeesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/employees/new" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><HREmployeeNewPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/employees/:id" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><HREmployeeDetailPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/noteworthy-events" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><NoteworthyEventsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/noteworthy-event-types" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><NoteworthyEventTypesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/positions" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EmployeePositionsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/org-chart" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><OrgChartPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/vacations" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><VacationManagementPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/overtime" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><OvertimeManagementPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/work-calendar" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EmployeeWorkCalendarPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/job-posts" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><JobPostsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/job-posts/new" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><JobPostFormPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/job-posts/:id" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><JobPostFormPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/job-posts/embed-guide" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><JobPostsEmbedGuide /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/applicants" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ApplicantsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/applicants/:id" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ApplicantDetailPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/human-resources/applicant-prompts" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ApplicantPromptsPage /></React.Suspense></ProtectedRoute>} />
      
      {/* Holdco Views Routes */}
      <Route path="/holdco-views" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><HoldcoViewsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/holdco-access-requests" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><HoldcoAccessRequestsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/holdco-views/create" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><CreateHoldcoViewPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/holdco-views/settings/components" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><HoldcoDashboardComponentsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/holdco-views/:viewId" element={<ProtectedRoute><ErrorBoundary><React.Suspense fallback={<LoadingFallback />}><HoldcoViewDashboardPage /></React.Suspense></ErrorBoundary></ProtectedRoute>} />
      <Route path="/holdco-views/:viewId/configure" element={<ProtectedRoute><ErrorBoundary><React.Suspense fallback={<LoadingFallback />}><HoldcoViewDashboardPage /></React.Suspense></ErrorBoundary></ProtectedRoute>} />
      <Route path="/holdco-views/validate/:requestId" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ValidateAccessRequestPage /></React.Suspense></ProtectedRoute>} />

      {/* Custom Reports */}
      <Route path="/custom-reports" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><CustomReportsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/custom-reports/create" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><CreateCustomReportPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/custom-reports/:reportId" element={<ProtectedRoute><ErrorBoundary><React.Suspense fallback={<LoadingFallback />}><CustomReportViewPage /></React.Suspense></ErrorBoundary></ProtectedRoute>} />
      
      {/* Public Job Application Route */}
      <Route path="/apply/:jobSlug" element={<React.Suspense fallback={<LoadingFallback />}><JobApplicationPage /></React.Suspense>} />
      
      {/* Articles Routes */}
      <Route path="/articles" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ArticlesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/articles/categories" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ArticleCategoriesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/articles/tags" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ArticleTagsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/articles/authors" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ArticleAuthorsPage /></React.Suspense></ProtectedRoute>} />
      
      {/* CMS Plugins Routes */}
      <Route path="/cms/plugins" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><CMSPluginsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/articles/media-library" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><MediaLibraryPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/articles/typography" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ArticleTypographyPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/articles/embed-guide" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ArticlesEmbedGuide /></React.Suspense></ProtectedRoute>} />
      <Route path="/articles/embed" element={<React.Suspense fallback={<LoadingFallback />}><ArticlesEmbedWidget /></React.Suspense>} />
      <Route path="/authors/embed-guide" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><AuthorsEmbedGuide /></React.Suspense></ProtectedRoute>} />
      <Route path="/tags/embed-guide" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><TagsEmbedGuide /></React.Suspense></ProtectedRoute>} />
      <Route path="/categories/embed-guide" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><CategoriesEmbedGuide /></React.Suspense></ProtectedRoute>} />
      <Route path="/articles/:id" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ArticleEditorPage /></React.Suspense></ProtectedRoute>} />
      {/* Internal Training Routes */}
      <Route path="/internal-training/prompts" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingPromptsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/dashboard" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingDashboardPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/programs" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingProgramsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/programs/new" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingProgramNewPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/programs/:programId/edit" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingProgramEditPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/programs/:programId/emails" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingProgramEmailsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/assignments" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingAssignmentsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/assignments/new" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingAssignmentNewPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/progress" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingProgressPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/needs" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingNeedsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/evaluation-categories" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EvaluationCategoriesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/evaluation-criteria" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EvaluationCriteriaPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training/evaluations" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EmployeeEvaluationsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training-shares" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingSharesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training-shares/:id/edit" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingShareGroupEditPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training-shares/:id/sequences" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingShareSequencesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/internal-training-shares-settings" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><InternalTrainingSharesSettingsPage /></React.Suspense></ProtectedRoute>} />
      
      {/* Meetings Routes */}
      <Route path="/meetings" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><MeetingsListPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/meetings/vote-now" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><VoteNowPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/meetings/new" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><CreateMeetingPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/meetings/:id" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><MeetingDetailPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/meetings/:id/edit" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><CreateMeetingPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/meetings/templates" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><MeetingTemplatesManager /></React.Suspense></ProtectedRoute>} />
      <Route path="/meetings/types" element={<OwnerAdminRoute><React.Suspense fallback={<LoadingFallback />}><MeetingTypesManager /></React.Suspense></OwnerAdminRoute>} />
      <Route path="/meetings/section-types" element={<OwnerAdminRoute><React.Suspense fallback={<LoadingFallback />}><MeetingSectionTypesManager /></React.Suspense></OwnerAdminRoute>} />
      <Route path="/meetings/settings" element={<OwnerAdminRoute><React.Suspense fallback={<LoadingFallback />}><MeetingSettingsPage /></React.Suspense></OwnerAdminRoute>} />
      
      {/* Survey Routes */}
      <Route path="/surveys" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveysPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/new" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveyBuilderPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/:surveyId" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveyDetailPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/:surveyId/edit" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveyBuilderPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/:surveyId/emails" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveyEmailsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/:surveyId/results" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveyResultsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/recipients" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveyRecipientsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/submissions" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveySubmissionsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/results" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveyResultsGlobalPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/email-tracking" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveyEmailTrackingPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/process" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveyProcessSubmissionsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/email-management" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveyEmailManagementPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/surveys/prompts" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><SurveyPromptsPage /></React.Suspense></ProtectedRoute>} />
      
      {/* Podcast Host Routes */}
      <Route path="/podcasthost" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastHostDashboard /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/shows" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastShowsPage /></React.Suspense></ProtectedRoute>} />
      {/* Static embed guide routes MUST come before dynamic :showId route */}
      <Route path="/podcasthost/shows/embed-guide" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastShowsEmbedGuide /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/shows/:showId" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastShowDetailPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/hosts" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastHostsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/episodes" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastEpisodesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/episodes/embed-guide" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastEpisodesEmbedGuide /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/episode/embed-guide" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastEpisodeEmbedGuide /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/guests" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastGuestsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/campaigns" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastCampaignsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/campaigns/:campaignId/emails" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastCampaignEmailsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/campaigns/:campaignId/email-settings" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastCampaignEmailSettingsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/email-analytics" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastEmailAnalyticsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/email-settings" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastEmailSettingsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/settings" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastSettingsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/podcasthost/prompts" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><PodcastPromptsPage /></React.Suspense></ProtectedRoute>} />
      
      {/* Events Module Routes */}
      <Route path="/events" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventsDashboard /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/events" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventsListPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/events/embed-guide" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventsEmbedGuide /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/events/:eventId" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventDetailPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/event/embed-guide" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventEmbedGuide /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/speakers" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventSpeakersPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/leads" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventLeadsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/settings" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventsSettingsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/public-path" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventsPublicPathPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/leads-webhook-settings" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventsLeadsWebhookSettingsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/campaigns" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventCampaignsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/campaigns/:campaignId/emails" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventCampaignEmailsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/campaigns/:campaignId/email-settings" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventCampaignEmailSettingsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/prompts" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventPromptsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/associations" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventAssociationsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/association-types" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventAssociationTypesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/certificates" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventCertificatesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/questions" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventsQuestionsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/certificate-templates" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventCertificateTemplatesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/email-analytics" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventEmailAnalyticsPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/events/email-settings" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><EventsEmailSettingsPage /></React.Suspense></ProtectedRoute>} />
      
      {/* Learning Center Shares Routes */}
      <Route path="/learning-center-shares" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><LearningCenterSharesPage /></React.Suspense></ProtectedRoute>} />
      <Route path="/learning-center-shares-settings" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><LearningCenterSharesSettingsPage /></React.Suspense></ProtectedRoute>} />
      
      {/* Role Permissions Route */}
      <Route path="/role-permissions" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><RolePermissionsPage /></React.Suspense></ProtectedRoute>} />
      
      {/* Product Mapping Route */}
      <Route path="/product-mapping" element={<ProtectedRoute><React.Suspense fallback={<LoadingFallback />}><ProductMappingPage /></React.Suspense></ProtectedRoute>} />
      {/* Public LMS Progress Page (no auth required) */}
      <Route path="/lms-progress/:shareCode" element={<React.Suspense fallback={<LoadingFallback />}><PublicLmsProgressPage /></React.Suspense>} />
      
      {/* Public Training Progress Page (no auth required) */}
      <Route path="/training-progress/:shareCode" element={<React.Suspense fallback={<LoadingFallback />}><PublicTrainingProgressPage /></React.Suspense>} />
      
      {/* 404 route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};
